import React, { useEffect, useState } from 'react';
/**
 * `Layout` is the application shell.
 *
 * Responsibilities:
 * - Resolves journey type from URL/path and loads site bootstrap data via
 *   `SiteApi.getSiteDetails` (user, brand, content, app config).
 * - Handles vanity URLs, suppression page bootstrap, and under-maintenance
 *   redirects.
 * - Sets i18n language based on country code and waits for translation
 *   readiness before rendering.
 * - Injects brand styles, favicon links, and tracking/third-party scripts via
 *   `Helmet`.
 * - Renders the app `Routing` component once bootstrap is complete.
 *
 * Exports `useJourney()` as a typed wrapper around `useOutletContext`.
 */
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { Journey, PageAbsolutePath } from '../../consts/Common';
import { SiteApi } from '../../api/SiteApi';
import { JourneyContext, SiteServer } from '../common/CommonTypes';
//@ts-ignore

import CircularProgress from '@mui/material/CircularProgress';
import { CommonValidation } from '../common/CommonValidation/commonValidation';
import i18next from 'i18next';
import { setCountryCode } from '../common/HelperMethods';
import { useCurrentPath } from './useCurrentPath';
import { CountryIsoCodes } from '../common/Constants';
import { useTranslation } from 'react-i18next';
import Routing from './Routing';
import { useAppDispatch, useAppSelector } from '../../app-state/Hooks';
import { setAppConfig } from '../../app-state/slices/ConfigSlice';
import { setBrand } from '../../app-state/slices/BrandSlice';
import { setContent } from '../../app-state/slices/ContentSlice';
import { resetLogoutReason, setUser } from '../../app-state/slices/UserSlice';
import { useIdleTimeout } from '../../hooks/useIdleTimeout';
import { logout } from '../../app-state/actions';
import IdleModals from './IdleModals';

const Layout = (): JSX.Element => {
  const userId = useAppSelector((s) => s.user.userId);
  const brandStyles = useAppSelector((s) => s.brand.brandStyles);
  const content = useAppSelector((s) => s.content);
  const countryCode = useAppSelector((s) => s.appConfig.countryCode);
  const isAppUnderMaintenance = useAppSelector(
    (s) => s.appConfig.isAppUnderMaintenance
  );
  const maintenanceMessage = useAppSelector(
    (s) => s.appConfig.maintenanceMessage
  );

  const [loading, setLoading] = useState(true);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const currentPath = useCurrentPath();
  const [translationsReady, setTranslationsReady] = useState(false);

  const { ready } = useTranslation();

  const idleTimeout = useAppSelector((s) => s.appConfig.idleTimeout);
  const loggedIn = useAppSelector((s) => s.user.loggedIn);
  const loggedOutReason = useAppSelector((s) => s.user.loggedOutReason);

  const handleIdleTimeout = () => {
    dispatch(logout({ reason: 'idle' }));
  };

  const { isWarning } = useIdleTimeout({
    enabled: loggedIn && idleTimeout?.enabled,
    timeoutMin: idleTimeout?.timeout,
    promptBeforeIdleMin: idleTimeout?.promptBeforeIdle,
    onTimeout: handleIdleTimeout
  });

  useEffect(() => {
    let urlLanguage = 'en';
    switch (countryCode.toLowerCase()) {
      case CountryIsoCodes.GB:
        urlLanguage = 'en';
        break;
      case CountryIsoCodes.DE:
      case CountryIsoCodes.AT:
        urlLanguage = 'de-DE';
        break;
      case CountryIsoCodes.ES:
        urlLanguage = 'es-ES';
    }

    i18next.changeLanguage(urlLanguage).then(() => setTranslationsReady(true));
  }, [countryCode]);

  useEffect(() => {
    if (isAppUnderMaintenance) {
      navigateToMaintenancePage();
    }
  }, [isAppUnderMaintenance]);

  useEffect(() => {
    const abortController = new AbortController();

    async function fetchSite(
      journeyType: number,
      instanceId: string,
      signal: AbortSignal,
      renewalSourceSystem: string,
      recordId?: string
    ) {
      const fetchSiteSuccess = (retrievedSite: SiteServer) => {
        if (retrievedSite?.brand?.brandVanityUrl) {
          journeyType = retrievedSite.brand.brandVanityJourney;
        }

        if (retrievedSite.isAppUnderMaintenance) {
          navigateToMaintenancePage();
          return;
        }

        if (retrievedSite.brand.brandStyles === '') {
          redirectToErrorPage(
            'This brand is not configured for use in this site',
            retrievedSite.countryCode
          );
          return;
        }

        if (retrievedSite.user.error) {
          redirectToErrorPage(
            retrievedSite.user.error,
            retrievedSite.countryCode
          );
          return;
        }

        if (
          !(
            journeyType === Journey.Renewals ||
            journeyType === Journey.Customer ||
            retrievedSite?.user?.isValid === true
          )
        ) {
          redirectToErrorPage(
            retrievedSite?.user?.error,
            retrievedSite.countryCode
          );
          return;
        }

        const {
          user: retrievedUser,
          brand: retrievedBrand,
          content: retrievedContent,
          ...retrievedAppConfig
        } = retrievedSite;
        dispatch(setUser({ ...retrievedUser }));
        dispatch(setBrand(retrievedBrand));
        dispatch(setContent(retrievedContent));
        dispatch(
          setAppConfig({
            ...retrievedAppConfig,
            journeyType,
            recordId,
            renewalSourceSystem
          })
        );

        if (retrievedSite.brand.googleTagManagerId) {
          const gtmId = retrievedSite.brand.googleTagManagerId;
          const cspNonce =
            document
              .querySelector('meta[name="csp-nonce"]')
              ?.getAttribute('content') ?? '';

          const script = document.createElement('script');
          script.nonce = cspNonce;
          script.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];
            w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
            var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
            j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
            f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${gtmId}');`;
          document.head.insertBefore(script, document.head.childNodes[0]);

          const noscript = document.createElement('noscript');
          noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
          document.body.insertBefore(noscript, document.body.childNodes[0]);
        }

        if (retrievedSite.brand.brandVanityUrl) {
          switch (retrievedSite.brand.brandVanityJourney) {
            case Journey.Renewals:
              setLoading(false);
              navigate(
                setCountryCode(
                  PageAbsolutePath.Renewals.Landing,
                  retrievedSite.countryCode
                )
              );
              break;
            case Journey.Customer:
              setLoading(false);
              navigate(
                setCountryCode(
                  PageAbsolutePath.Customer.Landing,
                  retrievedSite.countryCode
                )
              );
              break;
            default:
              redirectToErrorPage(
                'The vanity url does not map to a journey',
                retrievedSite.countryCode
              );
          }
          return;
        }

        setLoading(false);
      };

      const fetchSiteFailure = (error: string) => {
        const path = setCountryCode(PageAbsolutePath.Error, countryCode);
        navigate(path, { state: { error: 'User not found' } });
        console.log('User not found - ' + error);
        setLoading(false);
      };

      await new SiteApi().getSiteDetails(
        journeyType,
        instanceId,
        fetchSiteSuccess,
        fetchSiteFailure,
        signal
      );
    }

    if (isPageReload()) {
      const path = setCountryCode(
        PageAbsolutePath.Renewals.Landing,
        countryCode
      );
      navigate(path);
      setLoading(false);
      return;
    }

    // Standalone suppression page per brand
    if (currentPath === PageAbsolutePath.Suppression) {
      setLoading(false);
      return;
    }

    if (CommonValidation.isEmpty(userId)) {
      const params = getParams();
      const instanceId = params.get('InstanceID');
      const recordId = params.get('RecordID');
      const renewalSourceSystem = params.get('RenewalSourceSystem');

      const journeyType = getJourneyType(instanceId);

      if (journeyType === Journey.Initial) {
        const path = setCountryCode(PageAbsolutePath.Error, countryCode);
        navigate(path, {
          state: { error: 'User not found' }
        });
        setLoading(false);
        return;
      } else {
        fetchSite(
          journeyType,
          instanceId ?? '',
          abortController.signal,
          renewalSourceSystem ?? '',
          recordId ?? ''
        );
      }
    }

    return () => {
      abortController.abort();
    };
  }, []);

  const navigateToMaintenancePage = () => {
    const path = setCountryCode(PageAbsolutePath.UnderMaintenance, countryCode);
    navigate(path, {
      state: { error: maintenanceMessage }
    });
    setLoading(false);
  };

  const redirectToErrorPage = (errorMessage: string, countryCode: string) => {
    const path = setCountryCode(PageAbsolutePath.Error, countryCode);
    navigate(path, { state: { error: errorMessage } });
    setLoading(false);
  };

  const getParams = () => {
    const result = new URLSearchParams(search);

    return result;
  };

  const getJourneyType = (instanceId: string | null): number => {
    switch (true) {
      case instanceId && currentPath === PageAbsolutePath.Renewals.Landing:
        return Journey.CallCentre;
      case instanceId &&
        (currentPath === PageAbsolutePath.Landing || currentPath === ''):
        return Journey.DealerJourney;
      case currentPath === PageAbsolutePath.Renewals.Landing:
        return Journey.Renewals;
      case currentPath === PageAbsolutePath.Customer.Landing:
      case currentPath === PageAbsolutePath.Customer.PaymentAssistSuccess:
      case currentPath === PageAbsolutePath.Customer.PaymentAssistFailure:
        return Journey.Customer;
      case currentPath === PageAbsolutePath.PaymentAssistSuccess:
      case currentPath === PageAbsolutePath.PaymentAssistFailure:
        return Journey.DealerJourney;
      default:
        return Journey.Initial;
    }
  };

  const isPageReload = (): boolean => {
    const renewalPages = Object.values<string>(PageAbsolutePath.Renewals);
    const result = renewalPages.includes(window.location.pathname);
    let returnValue = false;

    if (result === true) {
      const navigationType = window.performance.navigation.type;
      if (navigationType == 1) {
        returnValue = true;
      }
    }

    return returnValue;
  };

  const iconUrl = content?.common?.faviconUrl ?? '';
  const force24Script = content?.force24Script ?? '';
  const allureScript = content?.allureScript ?? '';
  const clarityScript = content?.clarityScript ?? '';
  const croSource = content?.croSource ?? '';

  const nonce =
    document.querySelector('meta[name="csp-nonce"]')?.getAttribute('content') ??
    '';

  useEffect(() => {
    if (!force24Script) return;
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.nonce = nonce;
    script.textContent = force24Script;
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [force24Script, nonce]);

  useEffect(() => {
    if (!allureScript) return;
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.nonce = nonce;
    script.textContent = allureScript;
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [allureScript, nonce]);

  useEffect(() => {
    if (!clarityScript) return;
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.nonce = nonce;
    script.textContent = clarityScript;
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [clarityScript, nonce]);

  useEffect(() => {
    if (!croSource) return;
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = croSource;
    document.head.insertBefore(script, document.head.firstChild);
    return () => {
      document.head.removeChild(script);
    };
  }, [croSource]);

  if (loading || !translationsReady || !ready) {
    return (
      <div className='loading-container'>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <style nonce={nonce}>{brandStyles}</style>
        <link rel='apple-touch-icon' href={iconUrl} />
        <link rel='icon' type='image/png' sizes='512x512' href={iconUrl} />
        <link rel='icon' type='image/png' sizes='192x192' href={iconUrl} />
        <link rel='icon' type='image/png' sizes='32x32' href={iconUrl} />
        <link rel='icon' type='image/png' sizes='16x16' href={iconUrl} />
        <link rel='shortcut icon' href={iconUrl} />
      </Helmet>
      <Routing />
      <IdleModals
        isIdleWarningOpen={isWarning}
        isIdleExpiredOpen={!loggedIn && !!loggedOutReason}
        onIdleExpiredClose={() => dispatch(resetLogoutReason())}
      />
    </>
  );
};

export default Layout;

export function useJourney(): JourneyContext {
  return useOutletContext<JourneyContext>();
}
